const HEADER_SEARCH_DEBOUNCE_MS = 90;
const HEADER_SEARCH_MIN_CHARS = 1;

class HeaderSearchBar extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('[data-search-trigger]');
    this.panel = this.querySelector('[data-search-panel]');
    this.overlay = this.querySelector('[data-search-overlay]');
    this.inner = this.querySelector('[data-search-inner]');
    this.closeButton = this.querySelector('[data-search-close]');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('.ais-SearchBox-reset');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.lastQuery = '';
    this.queryDebounceTimer = null;
    this.isOpen = false;

    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnFocusIn = this.onFocusIn.bind(this);
  }

  connectedCallback() {
    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    if (this.input) {
      // Listener #1 — UI sync (reset button + immediate loading feedback).
      // Runs in capture phase so it fires before the base SearchForm's
      // debounced listener.
      this.input.addEventListener(
        'input',
        (event) => {
          this.syncResetButton();
          this.onInputFast(event);
        },
        true
      );
      this.syncResetButton();
    }

    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => {
        // Defer so SearchForm's reset handler runs first
        setTimeout(() => {
          this.syncResetButton();
          this.lastQuery = '';
          if (this.input) this.input.focus();
        });
      });
    }
  }

  onInputFast(event) {
    if (!this.predictiveSearch) return;

    const term = (this.input.value || '').trim();

    // Empty input → close the results panel and bail.
    if (term.length < HEADER_SEARCH_MIN_CHARS) {
      clearTimeout(this.queryDebounceTimer);
      this.lastQuery = '';
      if (typeof this.predictiveSearch.close === 'function') {
        this.predictiveSearch.close(true);
      }
      return;
    }

    if (term === this.lastQuery) return;
    this.lastQuery = term;

    this.predictiveSearch.setAttribute('loading', 'true');

    clearTimeout(this.queryDebounceTimer);
    this.queryDebounceTimer = setTimeout(() => {
      this.predictiveSearch.searchTerm = term;
      if (typeof this.predictiveSearch.getSearchResults === 'function') {
        this.predictiveSearch.getSearchResults(term);
      }
    }, HEADER_SEARCH_DEBOUNCE_MS);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundOnKeydown);
    document.removeEventListener('focusin', this.boundOnFocusIn);
    document.body.classList.remove('header-search-open');
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.panel.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('is-open');
    });

    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('header-search-open');

    document.addEventListener('keydown', this.boundOnKeydown);
    document.addEventListener('focusin', this.boundOnFocusIn);

    setTimeout(() => {
      if (this.input) {
        this.input.focus();
        const len = this.input.value.length;
        try {
          this.input.setSelectionRange(len, len);
        } catch (_) {
        }
      }
    }, 50);

    this.dispatchEvent(new CustomEvent('header-search:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('header-search-open');

    document.removeEventListener('keydown', this.boundOnKeydown);
    document.removeEventListener('focusin', this.boundOnFocusIn);

    const onTransitionEnd = (event) => {
      if (event.target !== this.inner) return;
      this.panel.hidden = true;
      this.inner.removeEventListener('transitionend', onTransitionEnd);
    };

    if (this.inner) {
      this.inner.addEventListener('transitionend', onTransitionEnd);
      setTimeout(() => {
        if (!this.isOpen) this.panel.hidden = true;
      }, 400);
    } else {
      this.panel.hidden = true;
    }

    if (this.trigger) {
      this.trigger.focus();
    }

    this.dispatchEvent(new CustomEvent('header-search:close', { bubbles: true }));
  }

  onKeydown(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      this.close();
    }
  }

  onFocusIn(event) {
    if (!this.isOpen || !this.inner) return;
    if (this.contains(event.target)) return;
    if (this.input) this.input.focus();
  }

  syncResetButton() {
    if (!this.resetButton || !this.input) return;
    if (this.input.value.length > 0) {
      this.resetButton.removeAttribute('hidden');
    } else {
      this.resetButton.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('header-search-bar')) {
  customElements.define('header-search-bar', HeaderSearchBar);
}
